import {
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";
import { useMasterPassword } from "../../components/MasterPasswordDialog/MasterPasswordContext";
import { enqueueSnackbar } from "notistack";

const fetchOderItems = (orderId) => {
    const params = {
        model: `Table Order`,
        name: orderId,
        method: `get_items`,
    };
    return axios.get(`method/frappe_helper.api.call`, { params });
};

export const useTableOrderItems = (orderId, onSuccess, onError) => {
    return useQuery(
        ["getTableOrderItems", orderId],
        async () => {
            const data = await fetchOderItems(orderId);
            return data.data.message;
        },
        {
            onSuccess,
            onError,
            enabled: !!orderId,
            cacheTime: 0,
            refetchOnMount: true,
        }
    );
};

export const useTableOrdersItems = (orders, onSuccess, onError) => {
    return useQueries(
        orders.map((order) => {
            return {
                queryKey: ["getTableOrderItemsParallel", order],
                queryFn: () => fetchOderItems(order),
            };
        }),
        {
            enabled: !!orders && orders.length > 0,
            cacheTime: 0,
        }
    );
};

const fetchTableOrders = (tableId) => {
    const params = {
        model: `Restaurant Object`,
        name: tableId,
        method: `orders_list`,
        args: "{}",
    };
    return axios.get(`method/frappe_helper.api.call`, { params });
};

const pushItemToOrder = async (orderId, item) => {
    try {
        const itemToSend = JSON.stringify(item);
        const params = {
            model: `Table Order`,
            name: orderId,
            method: `_push_item`,
            args: itemToSend,
        };

        const response = await axios.post(
            `method/frappe_helper.api.call`,
            params
        );
        return response.data;
    } catch (error) {
        throw new Error(error.message);
    }
};

export const usePushItemToOrderData = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orderId, item }) => {
            item.addons = JSON.stringify(item.addons);
            const data = await pushItemToOrder(orderId, item);
            return data;
        },
        {
            onSettled: (data, error, { orderId }) => {
                queryClient.invalidateQueries(["getTableOrderItems", orderId]);
                queryClient.refetchQueries(["getTableOrderItems", orderId]);
            },
        }
    );
};

const updateOrderItem = async (orderId, item, qty) => {
    try {
        const details = JSON.stringify({ identifier: item, qty: qty });

        const params = {
            model: `Table Order`,
            name: orderId,
            method: "update_entry_quantity",
            args: details,
        };

        const response = await axios.post(
            `method/frappe_helper.api.call`,
            params
        );
        return response.data;
    } catch (error) {
        throw new Error(error.message);
    }
};

export const useUpdateItemInOrderData = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orderId, item, qty }) => {
            const data = await updateOrderItem(orderId, item, qty);
            return data;
        },
        {
            onSettled: (data, error, { orderId }) => {
                queryClient.invalidateQueries(["getTableOrderItems", orderId]);
                queryClient.refetchQueries(["getTableOrderItems", orderId]);
            },
        }
    );
};

const removeItemFromOrder = async (orderId, item, masterPassword, qty, waste=false) => {
    try {
        let itemsToDelete = { item: item.identifier, qty: qty };
        if (waste) {
            itemsToDelete.waste = true;
        }
        const params = {
            model: `Table Order`,
            name: orderId,
            method: `delete_item`,
            args: JSON.stringify(itemsToDelete),
        };

        if (masterPassword) {
            params.master_password = masterPassword;
        }

        const response = await axios.post(
            masterPassword
                ? // ? `method/frappe_helper.api.escalate_privilege`
                  `method/restaurant_management.api.escalate_privilege`
                : `method/frappe_helper.api.call`,
            params
        );
        return response.data;
    } catch (error) {
        try {
            enqueueSnackbar(
                JSON.parse(JSON.parse(error.response.data._server_messages)[0])
                    .message,
                {
                    variant: "error",
                }
            );
            throw new Error(
                JSON.parse(error.response.data._server_messages)[0]
            );
        } catch {
            throw new Error(error.message);
        }
    }
};

export const useRemoveItemFromTableOrderData = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orderId, item, masterPassword, qty, waste }) => {
            const data = await removeItemFromOrder(
                orderId,
                item,
                masterPassword,
                qty,
                waste
            );
            return data;
        },
        {
            onSuccess: (data, { orderId }) => {
                // Refresh the query for the table order items.
                queryClient.invalidateQueries(["getTableOrderItems", orderId]);
                queryClient.refetchQueries(["getTableOrderItems", orderId]);
            },
        }
    );
};

const deleteOrder = async (orderId, masterPassword, notes, waste=false) => {
    try {
        const params = {
            model: `Table Order`,
            name: orderId,
            method: `delete_order`,
            args: JSON.stringify({ notes }),
        };

        if (waste) {
            params.waste = true;
        }

        if (masterPassword) {
            params.master_password = masterPassword;
        }

        const response = await axios.post(
            masterPassword
                ? `method/restaurant_management.api.escalate_privilege`
                : `method/frappe_helper.api.call`,
            params
        );
        return response.data;
    } catch (error) {
        try {
            enqueueSnackbar(
                JSON.parse(JSON.parse(error.response.data._server_messages)[0])
                    .message,
                {
                    variant: "error",
                }
            );
            throw new Error(
                JSON.parse(error.response.data._server_messages)[0]
            );
        } catch {
            throw new Error(error.message);
        }
    }
};

export const useDeleteOrder = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orderId, masterPassword, notes, waste }) => {
            const data = await deleteOrder(orderId, masterPassword, notes, waste);
            return data;
        },
        {
            onSuccess: (data, { orderId }) => {},
            onError: (error) => {
                try {
                    console.log(error.response.data._server_messages);
                    enqueueSnackbar(
                        JSON.parse(
                            JSON.parse(error.response.data._server_messages)[0]
                        ).message,
                        {
                            variant: "error",
                        }
                    );
                    throw new Error(
                        JSON.parse(error.response.data._server_messages)[0]
                    );
                } catch {
                    throw new Error(error.message);
                }
            },
        }
    );
};

export const useRemoveItemsFromTableOrderData = () => {
    return useMutation(async ({ items }) => {
        // Use useQueries to handle multiple parallel queries for item removals
        const removalQueries = items.map((item) => ({
            queryKey: ["removeItemFromOrder", item.order_name, item.entry_name], // Custom query key
            queryFn: () => removeItemFromOrder(item.order_name, item), // Custom query function
        }));

        await Promise.all(removalQueries.map((query) => query.queryFn()));

        // Optionally, you can return the result of each removal query if needed.
        return removalQueries.map((query) => query.data);
    });
};

const divideOrder = async (orderId, divisionPayload) => {
    // {"items":{"entry_9ef4-9c-4f3-bc8-8a4fe":{"name":"09f9cea9e5","qty":1,"identifier":"entry_obj_d83e-a1-434-ba9-76220"},"entry_6bf2a97e-4f74-4ba7-a1e3-2bebad025b13":{"name":"3c4d59b9ff","qty":1,"identifier":"entry_obj_75c6-c0-43f-b0f-eb158"}},"client":"obj_3a5d-bf-457-825-0c237"}
    try {
        const escapedPayload = JSON.stringify(divisionPayload);
        const params = {
            model: `Table Order`,
            name: orderId,
            method: `divide`,
            args: escapedPayload,
        };

        const response = await axios.post(
            `method/frappe_helper.api.call`,
            params
        );
        return response.data;
    } catch (error) {
        throw new Error(error.message);
    }
};

const onHouseOrder = async (orderId, notes, masterPassword) => {
    try {
        const params = {
            model: `Table Order`,
            name: orderId,
            method: `on_the_house`,
            args: JSON.stringify({ notes }),
        };

        if (masterPassword) {
            params.master_password = masterPassword;
        }

        const response = await axios.post(
            masterPassword
                ? `method/restaurant_management.api.escalate_privilege`
                : `method/frappe_helper.api.call`,
            params
        );
        return response.data;
    } catch (error) {
        console.log(error.response.data._server_messages);
        const errorMessage = JSON.parse(
            JSON.parse(error.response.data._server_messages)[0]
        ).message;
        enqueueSnackbar(errorMessage, {
            variant: "error",
        });
        throw new Error(error.message);
    }
};

const defferOrder = async (
    orderId,
    customer,
    discount_percentage,
    masterPassword
) => {
    try {
        const params = {
            model: `Table Order`,
            name: orderId,
            method: `defer_payment`,
            args: JSON.stringify({ customer: customer, discount_percentage }),
        };

        if (masterPassword) {
            params.master_password = masterPassword;
        }

        const response = await axios.post(
            masterPassword
                ? `method/restaurant_management.api.escalate_privilege`
                : `method/frappe_helper.api.call`,
            params
        );
        return response.data;
    } catch (error) {
        const errorMessage = JSON.parse(
            JSON.parse(error.response.data._server_messages)[0]
        ).message;
        enqueueSnackbar(errorMessage, {
            variant: "error",
        });
        throw new Error(error.message);
    }
};

export const useDivideOrderItemsData = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orderId, divisionPayload }) => {
            const data = await divideOrder(orderId, divisionPayload);
            return data;
        },
        {
            onSuccess: (data, { orderId }) => {
                queryClient.refetchQueries(["getTableOrderItems"]);
            },
        }
    );
};

export const useOrderItemOnHouse = () => {
    const queryClient = useQueryClient();
    const { showMasterPasswordDialog } = useMasterPassword();

    const mutation = useMutation(
        async ({ orderId, notes, masterPassword }) => {
            const data = await onHouseOrder(orderId, notes, masterPassword);
            return data;
        },
        {
            onSuccess: (data, { orderId }) => {
                queryClient.refetchQueries(["getTableOrderItems"]);
            },
        }
    );

    const mutate = async (params, options = {}) => {
        const { elevatedPrivileges, elevatedText, ...otherParams } = params;

        if (elevatedPrivileges) {
            try {
                const masterPassword = await showMasterPasswordDialog({
                    elevatedText,
                });

                return mutation.mutate(
                    { ...otherParams, masterPassword },
                    options
                );
            } catch (error) {
                if (options.onError) {
                    options.onError(
                        new Error("Master password dialog cancelled")
                    );
                }
                if (options.onSettled) {
                    options.onSettled(
                        null,
                        new Error("Master password dialog cancelled"),
                        params
                    );
                }
                return;
            }
        } else {
            return mutation.mutate(otherParams, options);
        }
    };

    return {
        ...mutation,
        mutate,
    };
};

export const useDefferOrderItem = () => {
    const queryClient = useQueryClient();
    const { showMasterPasswordDialog } = useMasterPassword();

    const mutation = useMutation(
        async ({ orderId, customer, discount_percentage, masterPassword }) => {
            const data = await defferOrder(
                orderId,
                customer,
                discount_percentage,
                masterPassword
            );
            return data;
        },
        {
            onSuccess: (data, { orderId }) => {
                queryClient.refetchQueries(["getTableOrderItems"]);
            },
        }
    );

    const mutate = async (params, options = {}) => {
        const { elevatedPrivileges, elevatedText, ...otherParams } = params;

        if (elevatedPrivileges) {
            try {
                const masterPassword = await showMasterPasswordDialog({
                    elevatedText,
                });

                return mutation.mutate(
                    { ...otherParams, masterPassword },
                    options
                );
            } catch (error) {
                if (options.onError) {
                    options.onError(
                        new Error("Master password dialog cancelled")
                    );
                }
                if (options.onSettled) {
                    options.onSettled(
                        null,
                        new Error("Master password dialog cancelled"),
                        params
                    );
                }
                return;
            }
        } else {
            return mutation.mutate(otherParams, options);
        }
    };

    return {
        ...mutation,
        mutate,
    };
};
